import React, { useState } from 'react';
import {
    Create,
    SimpleForm,
    TextInput,
    SelectInput,
    BooleanInput,
    DateTimeInput,
    required,
    useTranslate,
    RadioButtonGroupInput,
    ReferenceArrayInput,
    SelectArrayInput,
    Toolbar,
    SaveButton,
    useRedirect,
    useNotify,
    FormDataConsumer,
} from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Box, Alert, Typography, Card, CardContent, IconButton } from '@mui/material';
import {
    Message,
    Settings,
    People,
    Schedule,
    Notifications,
    ArrowBack,
    Info
} from '@mui/icons-material';
import ImageUploadField from './ImageUploadField';
import ActionTypeSelector from './ActionTypeSelector';
import { MessageMultiLanguageFields } from './MessageMultiLanguageFields';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';

const MESSAGE_KNOWN_FIELDS = new Set([
    'title', 'subtitle', 'content', 'content_html', 'type',
    'image_url', 'icon_name', 'tags', 'action_type',
    'action_target', 'action_label', 'target_type',
    'target_membership_levels', 'target_user_ids', 'target_data',
    'priority', 'status', 'valid_from', 'valid_until',
    'send_push', 'send_email', 'push_title', 'push_body',
    'push_subtitle', 'push_sound', 'push_badge_increment',
    'push_navigation_screen', 'push_navigation_params',
    'push_category', 'is_active', 'translations',
    'scheduled_at', 'expires_at',
]);

const mapMessageBackendPath = (path: string): string =>
    path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');

const messageFieldLabelKey = (field: string): string =>
    MESSAGE_KNOWN_FIELDS.has(field)
        ? `resources.messages.fields.${field}`
        : field;

const CreateToolbar = () => {
    return (
        <Toolbar
            sx={{
                backgroundColor: '#FAFAFA',
                borderTop: '1px solid #E0E0E0',
                px: { xs: 2, sm: 3 },
                py: 2,
                mt: 4,
                '& .MuiButton-root': {
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                },
            }}
        >
            <SaveButton
                variant="contained"
                sx={{
                    backgroundColor: '#00C853',
                    '&:hover': {
                        backgroundColor: '#00A843',
                        boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                    },
                }}
            />
        </Toolbar>
    );
};

const PageHeader = () => {
    const redirect = useRedirect();
    const translate = useTranslate();

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 4,
        }}>
            <IconButton
                onClick={() => redirect('/messages')}
                size="large"
                sx={{
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    },
                }}
                title={translate('resources.messages.create.backToList')}
            >
                <ArrowBack />
            </IconButton>
            <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                    {translate('resources.messages.create.title')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {translate('resources.messages.create.subtitle')}
                </Typography>
            </Box>
        </Box>
    );
};

const ImageUploadWrapper = ({ source }: { source: string }) => {
    const { setValue, watch } = useFormContext();
    const translate = useTranslate();
    const value = watch(source);

    return (
        <ImageUploadField
            source={source}
            value={value}
            onChange={(newValue) => setValue(source, newValue)}
            label={translate('resources.messages.fields.image')}
            helperText={translate('resources.messages.fields.imageHelp')}
        />
    );
};

const ActionTypeSelectorWrapper = () => {
    const { setValue, watch } = useFormContext();
    const actionType = watch('action_type') || 'none';
    const actionTarget = watch('action_target');

    return (
        <ActionTypeSelector
            actionType={actionType}
            actionTarget={actionTarget}
            onActionTypeChange={(value) => setValue('action_type', value)}
            onActionTargetChange={(value) => setValue('action_target', value)}
        />
    );
};

const MessageCreate = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const redirect = useRedirect();
    const [serverErrors, setServerErrors] = useState<Record<string, string>>({});

    return (
        <Create
            title={false}
            actions={false}
            component="div"
            redirect="list"
            mutationMode="pessimistic"
            mutationOptions={{
                onSuccess: () => {
                    setServerErrors({});
                    notify('resources.messages.notifications.createSuccess', { type: 'success' });
                    redirect('/messages');
                },
                onError: (error: any) => {
                    const apiError = error?.body || error;
                    const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
                    const fieldErrors: Record<string, string> = {};
                    const summaryLines: string[] = [];
                    if (rawErrors) {
                        for (const [path, msgs] of Object.entries(rawErrors)) {
                            const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                            const translated = translateServerMessage(rawMsg, translate);
                            const field = mapMessageBackendPath(path);
                            fieldErrors[field] = translated;
                            const label = translate(messageFieldLabelKey(field), { _: field });
                            summaryLines.push(`\u2022 ${label}: ${translated}`);
                        }
                    }
                    setServerErrors(fieldErrors);

                    const headerKey = apiError?.errorCode
                        ? `errors.${apiError.errorCode}`
                        : 'validation.formHasErrors';
                    const header = translate(headerKey, { _: apiError?.message || 'Error' });

                    if (summaryLines.length > 0) {
                        notify(`${header}\n${summaryLines.join('\n')}`, {
                            type: 'error',
                            multiLine: true,
                            autoHideDuration: 10000,
                        });
                    } else {
                        notify(header, { type: 'error', autoHideDuration: 6000 });
                    }
                }
            }}
            sx={{
                backgroundColor: 'transparent',
                '& .RaCreate-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <SimpleForm
                toolbar={<CreateToolbar />}
                sx={{
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                    padding: { xs: 0, sm: 'inherit' },
                }}
            >
                <ServerErrorBinder errors={serverErrors} />
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', '& .MuiFormControl-root': { width: '100%' }, pb: { xs: 10, sm: 2 } }}>
                    <PageHeader />

                    <Alert severity="info" sx={{ mb: 3 }}>
                        <Typography variant="body2">
                            {translate('resources.messages.createHint')}
                        </Typography>
                    </Alert>

                    {/* Message Content Section */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Message sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {translate('resources.messages.create.contentSection')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextInput
                                    source="title"
                                    label={translate('resources.messages.fields.title')}
                                    validate={[required()]}
                                    fullWidth
                                />
                                <TextInput
                                    source="content"
                                    label={translate('resources.messages.fields.content')}
                                    multiline
                                    rows={4}
                                    validate={[required()]}
                                    fullWidth
                                    helperText={translate('resources.messages.fields.contentHelp')}
                                />
                                <ImageUploadWrapper source="image_url" />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Message Properties */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Settings sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {translate('resources.messages.create.propertiesSection')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' }, flexDirection: { xs: 'column', sm: 'row' } }}>
                                <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                    <SelectInput
                                        source="type"
                                        label={translate('resources.messages.fields.type')}
                                        choices={[
                                            { id: 'announcement', name: translate('resources.messages.types.announcement') },
                                            { id: 'promotion', name: translate('resources.messages.types.promotion') },
                                            { id: 'system', name: translate('resources.messages.types.system') },
                                            { id: 'personal', name: translate('resources.messages.types.personal') },
                                            { id: 'alert', name: translate('resources.messages.types.alert') },
                                        ]}
                                        validate={[required()]}
                                        fullWidth
                                    />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                    <SelectInput
                                        source="priority"
                                        label={translate('resources.messages.fields.priority')}
                                        choices={[
                                            { id: 'low', name: translate('resources.messages.priorities.low') },
                                            { id: 'normal', name: translate('resources.messages.priorities.normal') },
                                            { id: 'high', name: translate('resources.messages.priorities.high') },
                                            { id: 'urgent', name: translate('resources.messages.priorities.urgent') },
                                        ]}
                                        validate={[required()]}
                                        fullWidth
                                    />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Targeting Section */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <People sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {translate('resources.messages.create.targetingSection')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <RadioButtonGroupInput
                                    source="target_type"
                                    label={translate('resources.messages.fields.targetType')}
                                    choices={[
                                        { id: 'all', name: translate('resources.messages.targetTypes.all') },
                                        { id: 'membership', name: translate('resources.messages.targetTypes.membership') },
                                        // Temporarily disabled individual targeting due to API issues
                                        // { id: 'individual', name: translate('resources.messages.targetTypes.individual') },
                                    ]}
                                    validate={[required()]}
                                />

                                <FormDataConsumer>
                                    {({ formData }) => (
                                        <>
                                            {/* Membership Level Selection - only show when membership targeting is selected */}
                                            {formData?.target_type === 'membership' && (
                                                <SelectArrayInput
                                                    source="target_data.membership_levels"
                                                    label={translate('resources.messages.fields.membershipLevels')}
                                                    choices={[
                                                        { id: 'standard', name: translate('resources.messages.memberships.standard') },
                                                        { id: 'silver', name: translate('resources.messages.memberships.silver') },
                                                        { id: 'gold', name: translate('resources.messages.memberships.gold') },
                                                        { id: 'platinum', name: translate('resources.messages.memberships.platinum') },
                                                        { id: 'vip', name: translate('resources.messages.memberships.vip') },
                                                    ]}
                                                    helperText={translate('resources.messages.fields.membershipHelp')}
                                                    validate={[required()]}
                                                />
                                            )}

                                            {/* Individual User Selection - temporarily disabled
                                            {formData?.target_type === 'individual' && (
                                                <ReferenceArrayInput
                                                    source="target_data.user_ids"
                                                    reference="app-users"
                                                    label={translate('resources.messages.fields.targetUsers')}
                                                >
                                                    <SelectArrayInput optionText="email" />
                                                </ReferenceArrayInput>
                                            )}
                                            */}
                                        </>
                                    )}
                                </FormDataConsumer>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Delivery Options */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Notifications sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {translate('resources.messages.create.deliverySection')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <ActionTypeSelectorWrapper />
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <BooleanInput
                                        source="send_push"
                                        label={translate('resources.messages.fields.sendPush')}
                                    />
                                    <BooleanInput
                                        source="send_email"
                                        label={translate('resources.messages.fields.sendEmail')}
                                    />
                                </Box>
                                <FormDataConsumer>
                                    {({ formData }) => formData?.send_push ? (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            <TextInput
                                                source="push_title"
                                                label={translate('resources.messages.fields.pushTitle', { _: 'Push Title' })}
                                                validate={[required()]}
                                                fullWidth
                                            />
                                            <TextInput
                                                source="push_body"
                                                label={translate('resources.messages.fields.pushBody', { _: 'Push Body' })}
                                                multiline
                                                rows={2}
                                                validate={[required()]}
                                                fullWidth
                                                helperText={translate('resources.messages.fields.pushBodyHelp', { _: 'Max 150 characters; longer text will be truncated.' })}
                                            />
                                        </Box>
                                    ) : null}
                                </FormDataConsumer>
                                <Alert severity="info">
                                    <Typography variant="body2">
                                        {translate('resources.messages.pushNotificationInfo')}
                                    </Typography>
                                </Alert>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Multi-language Translations Section */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <FormDataConsumer>
                                {({ formData }) => (
                                    <MessageMultiLanguageFields formData={formData} />
                                )}
                            </FormDataConsumer>
                        </CardContent>
                    </Card>

                    {/* Scheduling */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Schedule sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {translate('resources.messages.create.schedulingSection')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <RadioButtonGroupInput
                                    source="status"
                                    label={translate('resources.messages.fields.deliveryTime')}
                                    choices={[
                                        { id: 'draft', name: translate('resources.messages.statuses.draft') },
                                        { id: 'send_now', name: translate('resources.messages.statuses.sendNow') },
                                        { id: 'scheduled', name: translate('resources.messages.statuses.scheduled') },
                                    ]}
                                    defaultValue="draft"
                                />
                                <Box sx={{ display: 'flex', gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' }, flexDirection: { xs: 'column', sm: 'row' } }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <DateTimeInput
                                            source="valid_from"
                                            label={translate('resources.messages.fields.scheduledAt')}
                                            helperText={translate('resources.messages.fields.scheduledHelp')}
                                            fullWidth
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <DateTimeInput
                                            source="valid_until"
                                            label={translate('resources.messages.fields.expiresAt')}
                                            helperText={translate('resources.messages.fields.expiresHelp')}
                                            fullWidth
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </SimpleForm>
        </Create>
    );
};

export default MessageCreate;